import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Pages } from '../../../common/src/pages.ts';
import * as server_api from '../../../common/src/server_api.ts';
import { clearClientLocalStorage } from '../data/client_local_storage.ts';
import { useHousehold } from '../data/use_household.tsx';
import { getMonthlySubscriptionPrice } from '../util/billing.ts';
import { alert, confirm } from '../util/confirm.tsx';
import { getUrlForPage } from '../util/path.ts';
import { ContactSupportButton, PlainLinkOutlined } from '../util/shared.tsx';
import './help_page.css';
import { DialogPage } from './settings/dialog_page.tsx';

export const HelpPage = () => {
  const navigate = useNavigate();
  const { household } = useHousehold();

  // TODO(hewitt): Should use <Link> once we switch to React Router
  const onPrivacy = useCallback(async () => {
    await navigate(getUrlForPage(Pages.Privacy));
  }, [navigate]);

  const onInfoClick = useCallback(async () => {
    await navigate(getUrlForPage(Pages.About));
  }, [navigate]);

  // Real deletion (GitHub issue #4001): the server removes the household, its members and their
  // progress and cancels a web subscription; song views stay anonymised. An App Store subscription
  // is Apple's to cancel, hence the warning. Afterwards this device forgets everything.
  const onDeleteHousehold = useCallback(async () => {
    if (!household?.token) {
      return;
    }
    if (
      await confirm({
        confirmation: (
          <span>
            Delete your household from Sing Your Part?
            <br />
            <br />
            This removes your household, its members and their Learn progress. Your past song views stay in your
            church&apos;s statistics without your names.
            <br />
            <br />A subscription bought on the web is cancelled for you. A subscription bought through the App Store
            keeps renewing until you cancel it in your Apple ID settings.
          </span>
        ),
        confirmButtonText: 'Delete',
      })
    ) {
      try {
        await server_api.deleteMyHousehold({ token: household.token });
      } catch (e: any) {
        await alert({
          confirmation: <span>{e?.message || 'Your household could not be deleted. Please try again later.'}</span>,
        });
        return;
      }
      clearClientLocalStorage();
      window.location.href = '/';
    }
  }, [household]);

  const price = getMonthlySubscriptionPrice();
  return (
    <DialogPage title="Help" onInfoClick={() => void onInfoClick()}>
      {/* DialogPage's InnerPageContent scrolls; minHeight keeps the template background filling short viewports */}
      <div className="content" style={{ minHeight: '100%' }}>
        <div className="contentBlock centeredContent">
          <span>Questions or feedback?</span>
          <span>We would love to hear from you!</span>
          <ContactSupportButton />
        </div>
        <CustomerSegmentFeatures
          src={`https://singyourpart.app/billing/app/#customer-segment-features?monthly-price-override=${price}`}
          sandbox="allow-popups allow-top-navigation allow-scripts allow-popups-to-escape-sandbox"
        />
        <div className="contentBlock">
          <h2>Why Sing Your Part?</h2>
          God commands each of us to praise Him joyfully with our voice (Psalm 100), yet the music we sing in church can
          be difficult to access outside of church. With Sing Your Part, the music you will sing this Sunday is just two
          taps away on your phone, tablet or computer.
        </div>
        <div className="contentBlock">
          <h2>Why is music important?</h2>
          Read these short books to find out.
          <br />
          {/*<div style={{display: 'grid', gridTemplateColumns: '4fr 2fr 1fr 1fr', gap: '5px', paddingTop: '10px'}}>*/}
          <div>
            <div style={{ paddingTop: '10px' }}>
              <b>Church Music and the Other Kinds</b>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <div style={{ width: '17ch' }}>by Douglas Wilson</div>
              <div>
                <a
                  style={{ color: 'blue' }}
                  href="https://canonpress.com/products/church-music-and-the-other-kinds?srsltid=AfmBOopbrWnWsX7j3709TibO4DT_Hr8ICVEGi5chSI1MD7VcT2FkH9uU"
                >
                  Paper
                </a>
              </div>
              <div>
                <a
                  style={{ color: 'blue' }}
                  href="https://www.audible.com/pd/Church-Music-and-the-Other-Kinds-Audiobook/B0BX75WTK6"
                >
                  Audio
                </a>
              </div>
            </div>

            <div style={{ paddingTop: '10px' }}>
              <b>Sing!</b>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <div style={{ width: '17ch' }}>by Keith & Kristyn Getty</div>
              <div>
                <a
                  style={{ color: 'blue' }}
                  href="https://gettymusic.store/collections/books/products/sing-book-by-keith-kristyn"
                >
                  Paper
                </a>
              </div>
              <div>
                <a style={{ color: 'blue' }} href="https://www.audible.com/pd/Sing-Audiobook/B074HCD658">
                  Audio
                </a>
              </div>
            </div>
          </div>
        </div>
        <div className="contentBlock">
          <h2>Time Commitment</h2>
          Learning to sing your part only takes 5-10 minutes per day. But, to make progress, you need to be consistent.
          Find a time of the day when you will remember to practice. Choose one relatively simple song to start with
          (e.g. &quot;Amazing Grace&quot; or &quot;Be Thou My Vision&quot;).
          <br />
          <br />
          Do not expect overnight results! It might take a couple of months to master your first song. However, the
          second song will take less time, and the more you do it, the faster you will be able to acquire new songs!
        </div>
        <div className="contentBlock">
          <h2>Voice Range</h2>
          First, determine your voice range (soprano, alto, tenor, or bass). Sopranos are high female voices, and altos
          are low; tenors are high male voices and basses are low. If you&apos;re not sure which part you are best
          suited for, ask a musical friend to help you figure this out.
        </div>
        <div className="contentBlock">
          <h2>Singing</h2>
          To learn your part, start by opening up a song. Turn up the volume on your part (e.g. tenor) and turn down all
          the other parts. Adjust the tempo to a pace you are comfortable with. To sing along with the words, swipe to
          the right to view the music. Once you feel confident about singing your part on its own, slowly begin to turn
          up the volume of the melody (usually soprano).
          <br />
          <br />
          This will be challenging; once you hear the melody, you might be inclined to sing that instead of your own
          part. Keep the melody quiet enough for you to be able to hear your part, and as you gain confidence, gradually
          turn the melody up louder.
          <br />
          <br />
          When you can still sing your part while the melody is at full volume, congratulations - you are doing very
          well! However, you are not quite there yet. The next step is to gradually add in the remaining parts. If you
          can still sing your part with all the other parts at full volume, then the final step is to begin turning down
          your own part. Once your part is barely audible and you are still able to sing against the others, you are on
          the road to mastery.
          <br />
          <br />
          Again, this process takes time. Stick with it, though; before you know it, you will be able to sing your part
          with complete confidence!
        </div>
        <div className="contentBlock">
          <h2>Singing on Sunday Morning</h2>
          As singers, maintaining vocal health is key to delivering your best performance—especially on Sunday mornings
          when your voice might not be fully warmed up. Here are three essential tips to help you prepare:
          <br />
          <br />
          <ol>
            <li>
              <b>Hydrate Consistently.</b> Staying well-hydrated throughout the week is crucial for optimal vocal
              function. Make it a habit to drink water regularly and avoid dehydrating beverages like caffeine or
              alcohol, especially the day before a performance.
            </li>
            <li>
              <b>Warm Up Gradually.</b> Start your day with gentle vocal exercises. Begin with simple hums and lip
              trills before progressing to scales and dynamic range exercises. This gradual approach helps loosen up
              your vocal cords and reduces the risk of strain.
            </li>
            <li>
              <b>Incorporate Natural Remedies.</b> Keep your voice soothed and ready with trusted natural remedies like
              throat sprays and drops. We recommend Vocal Eze’s line of throat wellness products, available at{' '}
              <a href="https://vocaleze.com" rel="noreferrer">
                vocaleze.com
              </a>
              . Their soothing, all-natural formulas are designed to support vocal health and provide relief when you
              need it most.
            </li>
          </ol>
          <br />
          By following these tips, you’ll ensure that your voice is prepped and ready for a powerful and confident
          performance!
        </div>
        <div className="contentBlock">
          <h2>Churches</h2>
          If your church is not in the list, ask your music leader send an email to{' '}
          <a rel="noreferrer" href="mailto:support@crescendosw.com">
            support@crescendosw.com
          </a>{' '}
          with a request for your church to be added. Once we add your church, all that your music leader will need to
          do is submit their song list every week, which takes less than five minutes. This service is entirely free of
          charge.
          <br />
          <br />
          If your church is listed in Sing Your Part, but your weekly song list is empty, talk to your music leader
          about adding songs to Sing Your Part each week so that you can practice ahead of time for Sunday worship.
        </div>
        <div className="contentBlock">
          <h2>Feedback</h2>
          If you have questions, comments, or feedback about the app, please email us at
          <a href="mailto:support@crescendosw.com"> support@crescendosw.com</a>. Your input helps us make Sing Your Part
          better for everyone.
        </div>
        <div className="contentBlock">
          <h2>FAQ</h2>
          <ul>
            <li>What if I can&apos;t match pitch?</li>
            <br />
            <ul>
              <li>
                If you&apos;re having trouble matching pitch with the music, it would be worth it to spend time
                practicing this outside of Sing Your Part. If you have access to a keyboard, play individual notes and
                try matching them as best you&apos;re able. Doing this should help you improve!
              </li>
            </ul>
            <br />
            <br />
            <li>The hymnal my church uses isn&apos;t in Sing Your Part. How can I add it?</li>
            <br />
            <ul>
              <li>
                We are always trying to expand our hymnal library. If the hymnal your church uses isn&apos;t available
                in Sing Your Part, email us at <a href="mailto:support@crescendosw.com">support@crescendosw.com</a>, and
                we can add your hymnal to our list.
              </li>
            </ul>
            <br />
            <br />
            <li>I don’t know how to read sheet music. What can I do?</li>
            <br />
            <ul>
              <li>
                Don&apos;t worry; lots of people can’t read sheet music. You don&apos;t need to know music theory to
                learn your part, but it can make the process a lot easier. As you practice singing along with the words
                on the sheet music, pay attention to the notes on the staff (the set of lines that run across the page).
                You&apos;ll begin to notice patterns: long notes look different from short ones, and the notes are
                placed higher and lower on the staff to indicate changes in pitch. In addition to this, try looking up
                the basics of music theory. Getting a grasp on the subject will help you approach new music more easily.
              </li>
            </ul>
          </ul>
        </div>
        <div className="contentBlock">
          <HelpContent>
            <PlainLinkOutlined href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/">
              Terms of Use
            </PlainLinkOutlined>
            <PlainLinkOutlined onClick={onPrivacy}>Privacy Policy</PlainLinkOutlined>
            {household && <DeleteButton onClick={onDeleteHousehold}>Delete Household</DeleteButton>}
          </HelpContent>
        </div>
      </div>
    </DialogPage>
  );
};

const HelpContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: center;
`;

const CustomerSegmentFeatures = styled.iframe`
  width: 100%;
  height: 600px;
  border: none;
`;

export const DeleteButton = styled.button`
  margin-top: 10px;
  padding: 5px 10px;
  border: 3px solid var(--color-text);
  background-color: var(--color-background);
  color: red;
  border-radius: 15px;
  cursor: pointer;
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 1.3em;
  text-decoration: none;
  width: fit-content;

  &:hover {
    background-color: lightcoral;
    color: var(--color-text);
  }
`;
